import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../hooks/useAuth';
import LoadingSpinner from '../shared/LoadingSpinner';
import { X, Check } from 'lucide-react';

export default function ClientInvitePage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { session, signUp, signIn } = useAuth();

  const [invite, setInvite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [form, setForm] = useState({ firstName: '', lastName: '', password: '' });
  const [submitting, setSubmitting] = useState(false);
  const [hasAccount, setHasAccount] = useState(false);

  // Load invitation
  useEffect(() => {
    (async () => {
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000/api';
      const res = await fetch(`${API_URL}/invitations/${token}`);

      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        setError(res.status === 410
          ? 'This invitation has expired. Please contact your provider for a new one.'
          : body.error || 'This invitation is invalid or has already been used.');
        setLoading(false);
        return;
      }

      const data = await res.json();
      setInvite(data);
      setLoading(false);
    })();
  }, [token]);

  // If already logged in AS the invited user, accept directly
  useEffect(() => {
    if (session && invite && !submitting) {
      // Only auto-accept if the logged-in user's email matches the invite
      const loggedInEmail = session.user?.email?.toLowerCase();
      const inviteEmail = invite.email?.toLowerCase();
      if (loggedInEmail && inviteEmail && loggedInEmail === inviteEmail) {
        (async () => {
          setSubmitting(true);
          const { error: err } = await supabase.rpc('accept_invitation', {
            invitation_token: token,
          });
          if (err) {
            setError(err.message);
            setSubmitting(false);
            return;
          }
          navigate('/client', { replace: true });
        })();
      }
      // If logged in as someone else, just show the form (they'll need to sign in as the client)
    }
  }, [session, invite]); // eslint-disable-line

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);

    // Use server endpoint — handles auth user creation + invitation acceptance in one call
    const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000/api';
    try {
      const res = await fetch(`${API_URL}/invitations/${token}/accept`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          password: form.password,
          first_name: form.firstName || '',
          last_name: form.lastName || '',
        }),
      });

      const result = await res.json();

      if (!res.ok) {
        if (result.error?.includes('Account exists')) {
          setHasAccount(true);
        }
        setError(result.error || 'Something went wrong');
        setSubmitting(false);
        return;
      }

      // Set the session from the server response
      if (result.session) {
        await supabase.auth.setSession({
          access_token: result.session.access_token,
          refresh_token: result.session.refresh_token,
        });
      }
    } catch (err) {
      setError(err.message);
      setSubmitting(false);
      return;
    }

    navigate('/client', { replace: true });
  };

  const update = (field, value) => setForm(prev => ({ ...prev, [field]: value }));
  const inputClass = 'w-full px-3.5 py-[11px] bg-tertiary border border-strong rounded-[10px] text-primary-c text-[13px] font-[inherit] transition-all focus:outline-none focus:border-[#0DA7CA] focus:shadow-[0_0_0_3px_rgba(13, 167, 202,0.15)]';

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-primary">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error && !invite) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-primary p-4">
        <div className="w-full max-w-[420px] text-center">
          <div className="w-[48px] h-[48px] bg-danger-soft text-danger rounded-[14px] flex items-center justify-center mx-auto mb-4">
            <X size={24} strokeWidth={2.5} />
          </div>
          <h1 className="text-[22px] font-bold text-primary-c mb-2">Invitation Issue</h1>
          <p className="text-[13px] text-muted-c mb-6">{error}</p>
          <Link to="/login" className="text-[13px] font-semibold text-accent-light hover:text-accent transition-colors">
            Go to sign in
          </Link>
        </div>
      </div>
    );
  }

  if (submitting) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-primary">
        <div className="text-center">
          <LoadingSpinner size="lg" className="mb-4" />
          <p className="text-[13px] text-muted-c">Setting up your portal...</p>
        </div>
      </div>
    );
  }

  const orgName = invite?.organization?.name || 'your provider';

  return (
    <div className="min-h-screen flex items-center justify-center bg-primary p-4">
      <div className="w-full max-w-[440px]">
        {/* Header */}
        <div className="flex flex-col items-center mb-8">
          <img src="/logo.svg" alt="MediLink" className="h-[64px] w-auto mb-4" />
          <h1 className="text-[22px] font-bold text-primary-c">Welcome to your care portal</h1>
          <p className="text-[13px] text-muted-c mt-1 text-center">
            <strong className="text-primary-c">{orgName}</strong> has set up a portal for you to view your appointments
          </p>
        </div>

        {/* Card */}
        <div className="bg-elevated border border-theme rounded-xl p-6 md:p-7 shadow-lg">
          {/* What you'll get */}
          <div className="bg-accent-glow rounded-[10px] p-4 mb-5">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-lg bg-accent flex items-center justify-center text-white flex-shrink-0 mt-0.5"><Check size={16} strokeWidth={2.5} /></div>
              <div className="text-[12px] text-secondary-c leading-relaxed">
                <strong className="text-primary-c block mb-1">Your portal will include:</strong>
                Appointment schedule &bull; Provider details &bull; Email reminders &bull; Care team updates
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {error && invite && (
              <div className="px-4 py-3 rounded-[10px] bg-danger-soft text-danger text-[13px] font-medium">
                {error}
              </div>
            )}

            {/* Email (read-only) */}
            <div>
              <label className="block text-[12px] font-semibold text-secondary-c mb-2">Email</label>
              <input
                type="email"
                className={inputClass + ' opacity-60'}
                value={invite?.email || ''}
                readOnly
              />
            </div>

            {!hasAccount && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[12px] font-semibold text-secondary-c mb-2">First Name</label>
                  <input type="text" className={inputClass} value={form.firstName} onChange={e => update('firstName', e.target.value)} placeholder="Your first name" />
                </div>
                <div>
                  <label className="block text-[12px] font-semibold text-secondary-c mb-2">Last Name</label>
                  <input type="text" className={inputClass} value={form.lastName} onChange={e => update('lastName', e.target.value)} placeholder="Your last name" />
                </div>
              </div>
            )}

            <div>
              <label className="block text-[12px] font-semibold text-secondary-c mb-2">
                {hasAccount ? 'Password' : 'Create a Password'}
              </label>
              <input
                type="password"
                className={inputClass}
                placeholder={hasAccount ? 'Enter your password' : 'At least 8 characters'}
                value={form.password}
                onChange={e => update('password', e.target.value)}
                required
                autoFocus
              />
            </div>

            <button
              type="submit"
              disabled={submitting}
              className="w-full py-[11px] rounded-[10px] text-[13px] font-semibold text-white gradient-accent shadow-glow-lg hover:-translate-y-0.5 transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0"
            >
              {submitting ? 'Setting up...' : hasAccount ? 'Sign In' : 'Set Up My Account'}
            </button>
          </form>

          {!hasAccount && (
            <button
              onClick={() => setHasAccount(true)}
              className="w-full mt-3 text-[12px] text-muted-c hover:text-accent-light transition-colors text-center"
            >
              Already have an account? Sign in instead
            </button>
          )}
          {hasAccount && (
            <button
              onClick={() => { setHasAccount(false); setError(''); }}
              className="w-full mt-3 text-[12px] text-muted-c hover:text-accent-light transition-colors text-center"
            >
              Don't have an account? Create one
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
